import React, { useCallback, useEffect, useState } from "react";
import {
  API,
  defaultLimitPagination,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { useLocation, useNavigate } from "react-router-dom";
import {
  formatMobile,
  // formatName,
  formatSubcontractorName,
} from "../../lib/Misc";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import { debounce } from "lodash";
import TrustActionButton from "./TrustActionButton";
import MainBody from "../../components/MainBody/MainBody";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import AddEditTrust from "./AddEditTrust";
import { LucidePlusCircle } from "lucide-react";
import sortCaret from "../../components/SortCaret";
import { useOrganization } from "../../lib/context/OrganisationContext";
import moment from "moment";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/use-toast";

const Trusts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [trustsList, setTrustsList] = useState([]);
  const [trustLoading, setTrustLoading] = useState(false);
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [edit, setEdit] = useState(false);
  const [trustId, setTrustId] = useState(null);
  const [showAddEditTrust, setShowAddEditTrust] = React.useState(false);
  const [canCreate, setCanCreate] = useState(null);

  const { organizationData } = useOrganization();

  const toggleShowAddEditTrust = () => {
    if (showAddEditTrust) {
      setEdit(false);
      setTrustId(null);
    }
    setShowAddEditTrust(!showAddEditTrust);
  };

  const handleDelete = () => {
    fetchTrusts(limit, page);
  };

  /**
   * Fetch Trust API
   */
  const fetchTrusts = useCallback(
    async (passLimit, passPage) => {
      try {
        if (organizationData?.organizationId) {
          setTrustLoading(true);
          const { data } = await get(
            `${API.GET_TRUSTS}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&organization_id=${organizationData?.organizationId}`
          );
          setTrustLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const trustsList = data.data.data;
          setCanCreate(data.can_create);
          setTrustsList(trustsList);
          return data.data;
        }
      } catch (error) {
        setTrustsList([]);
        setTrustLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortField, sortOrder, organizationData?.organizationId]
  );

  /**
   *
   * @param {Number of Total Records} quantity
   * @returns trusts List
   */
  const trustsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: trustsList[i]?.id,
        trust_id: trustsList[i]?.id ? trustsList[i]?.id : "",
        name: organizationData?.organizationData?.is_subscribed ? (
          <span
            onClick={() => {
              navigate(`/trusts/${trustsList[i]?.id}/dashboard`);
            }}
            className="hover:underline hover:cursor-pointer"
          >
            {formatSubcontractorName(trustsList[i])}
          </span>
        ) : (
          <> {formatSubcontractorName(trustsList[i])}</>
        ),
        email: trustsList[i]?.email,
        mobile: trustsList[i]?.mobile
          ? formatMobile(trustsList[i]?.mobile)
          : "-",
        created_at: trustsList[i]?.created_at
          ? moment(trustsList[i]?.created_at, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY"
            )
          : "-",
        launch: (
          <span className="flex items-center gap-2 justify-center">
            {organizationData?.organizationData?.is_subscribed ? (
              <Button
                className="px-2 h-6"
                onClick={() => {
                  navigate(`/trusts/${trustsList[i]?.id}/dashboard`);
                }}
              >
                Launch
              </Button>
            ) : (
              <Button
                className="bg-black dark:bg-white dark:text-black px-2 h-6"
                onClick={() => {
                  navigate(`/general-settings?tab=subscription`);
                }}
              >
                Subscribe
              </Button>
            )}{" "}
          </span>
        ),
        action: (
          <TrustActionButton
            id={trustsList[i]?.id}
            handleDelete={handleDelete}
            setEdit={setEdit}
            setTrustId={setTrustId}
            toggleShowAddEditTrust={toggleShowAddEditTrust}
            orgId={organizationData?.organizationId}
          />
        ),
      });
    }
    return items;
  };
  const trusts = trustsGenerator(trustsList?.length);

  /**
   * Table Heading Data
   */
  const columns = [
    {
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "phone",
      text: "Phone No.",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "mobile",
      text: "Mobile No.",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      text: "Created Date",
      dataField: "created_at",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      align: "center",
      headerAlign: "center",
      text: "Launch",
      dataField: "launch",
    },
    {
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      // style: { width: "127px" },
      headerClasses: THClasses,
      classes: TDClasses,
    },
  ];

  /**
   * Table Change For Sorting
   */
  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `/trusts?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchTrusts();
  }, [fetchTrusts]);

  /**
   * Search
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    if (nextValue?.length >= 2 || nextValue?.length === 0) {
      debouncedSave(nextValue);
    }
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  /**
   * Trusts Limit Per Page
   */
  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchTrusts(limit, 1);
  };

  /**
   * Pagination
   */
  const handleChangePage = (page) => {
    setPage(page);
    fetchTrusts(limit, page);
  };

  return (
    <MainBody
      searchParamData={searchParamData}
      handleClearSearch={handleClearSearch}
      handleSearchChange={handleSearchChange}
      actionContent={
        <>
          {canCreate || edit === true ? (
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Trust" : "Add Trust"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setShowAddEditTrust(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add a Trust Account</span>
                </div>
              }
              className={""}
              open={showAddEditTrust}
              buttonClassName={""}
              setOpen={setShowAddEditTrust}
              write={true}
            >
              <AddEditTrust
                edit={edit}
                toggleShowAddEditTrust={toggleShowAddEditTrust}
                trustID={trustId}
                fetchTrusts={fetchTrusts}
                orgId={organizationData?.organizationId}
              />
            </ResponsiveDrawerForm>
          ) : (
            <Button
              onClick={() => {
                toast({
                  variant: "success",
                  description: "Please upgrade your plan to add more trusts.",
                  duration: 3000,
                });
              }}
            >
              <div className="flex items-center gap-1">
                <LucidePlusCircle size={18} />
                <span>Add a Trust Account</span>
              </div>
            </Button>
          )}
        </>
      }
      loading={trustLoading}
    >
      <div className="custom-table">
        <div className="table-body-class">
          <BootstrapTable
            keyField="trust_id"
            remote
            data={trusts}
            columns={columns}
            onTableChange={handleTableChange}
            rowClasses={TRClasses}
            noDataIndication="No Data Found"
            classes={TableClasses}
            wrapperClasses={TableWrapClasses}
          />
        </div>

        <Pagination
          total={total}
          limit={parseInt(limit)}
          currentPage={page}
          updateLimit={handleUpdateLimit}
          updatePage={handleChangePage}
          from={from}
          to={to}
        />
      </div>
    </MainBody>
  );
};

export default Trusts;
